import React from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Info, Lightbulb, Plus, Trash2 } from 'lucide-react';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Start Time Interval options - how frequently a new booking can start
const START_INTERVAL_OPTIONS = [
  { label: '10 minutes', value: 10 },
  { label: '15 minutes', value: 15 },
  { label: '30 minutes', value: 30 },
  { label: '45 minutes', value: 45 },
  { label: '1 hour', value: 60 },
];

const BUFFER_OPTIONS = [
  { label: 'No gap', value: 0 },
  { label: '5 minutes', value: 5 },
  { label: '10 minutes', value: 10 },
  { label: '15 minutes', value: 15 },
  { label: '20 minutes', value: 20 },
  { label: '30 minutes', value: 30 },
  { label: '45 minutes', value: 45 },
  { label: '1 hour', value: 60 },
];

/**
 * AvailabilityScheduler - Manages service availability with Fluid Blocking support
 * Supports multiple time ranges per day.
 */
const AvailabilityScheduler = ({ 
  availability, 
  onChange, 
  serviceDuration = null,
  variants = [],
  hideSchedulingSettings = false,
}) => {
  const { 
    business_hours = [], 
    slot_duration = 30,
    bookings_per_slot = 1,
    buffer_between_bookings = 0 
  } = availability || {};

  const start_time_interval = slot_duration;
  const concurrent_sessions = bookings_per_slot;

  const initializeBusinessHours = () => {
    if (business_hours.length === 0) {
      const defaultHours = DAYS.map(day => ({
        day,
        enabled: day !== 'Sunday' && day !== 'Saturday',
        from_time: '09:00',
        to_time: '18:00',
        time_ranges: [{ from: '09:00', to: '18:00' }],
      }));
      onChange({ 
        business_hours: defaultHours, 
        slot_duration: 30,
        bookings_per_slot: 1,
        buffer_between_bookings: 0
      });
    }
  };

  React.useEffect(() => {
    initializeBusinessHours();
  }, []);

  const updateBusinessHour = (index, field, value) => {
    const updated = [...business_hours];
    updated[index][field] = value;
    // Keep from_time/to_time in sync with first/last range
    if (field === 'time_ranges' && Array.isArray(value) && value.length > 0) {
      updated[index].from_time = value[0].from;
      updated[index].to_time = value[value.length - 1].to;
    }
    onChange({ ...availability, business_hours: updated });
  };

  // Get time_ranges for a day, falling back to single from/to
  const getTimeRanges = (hour) => {
    if (hour.time_ranges && hour.time_ranges.length > 0) return hour.time_ranges;
    return [{ from: hour.from_time || '09:00', to: hour.to_time || '18:00' }];
  };

  const addTimeRange = (dayIndex) => {
    const ranges = getTimeRanges(business_hours[dayIndex]);
    const lastTo = ranges[ranges.length - 1]?.to || '18:00';
    const [h, m] = lastTo.split(':').map(Number);
    const newFrom = `${String(Math.min(h + (m >= 30 ? 1 : 0), 23)).padStart(2, '0')}:${m >= 30 ? '00' : '30'}`;
    const newTo = `${String(Math.min(h + 2, 23)).padStart(2, '0')}:${lastTo.split(':')[1]}`;
    updateBusinessHour(dayIndex, 'time_ranges', [...ranges, { from: newFrom, to: newTo }]);
  };

  const removeTimeRange = (dayIndex, rangeIndex) => {
    const ranges = getTimeRanges(business_hours[dayIndex]);
    if (ranges.length <= 1) return;
    const updated = ranges.filter((_, i) => i !== rangeIndex);
    updateBusinessHour(dayIndex, 'time_ranges', updated);
  };

  const updateTimeRange = (dayIndex, rangeIndex, field, value) => {
    const ranges = [...getTimeRanges(business_hours[dayIndex])];
    ranges[rangeIndex] = { ...ranges[rangeIndex], [field]: value };
    updateBusinessHour(dayIndex, 'time_ranges', ranges);
  };

  const updateStartInterval = (value) => {
    onChange({ ...availability, slot_duration: parseInt(value) });
  };

  const updateConcurrentSessions = (value) => {
    onChange({ ...availability, bookings_per_slot: parseInt(value) || 1 });
  };

  const updateBufferTime = (value) => {
    onChange({ ...availability, buffer_between_bookings: parseInt(value) || 0 });
  };

  const getDurationMismatchTip = () => {
    const durations = [];
    if (serviceDuration && serviceDuration > 0) durations.push(serviceDuration);
    if (variants && variants.length > 0) {
      variants.forEach(variant => {
        if (variant.options) {
          variant.options.forEach(opt => {
            if (opt.name && /\d+\s*(min|hour|hr)/i.test(opt.name)) {
              const match = opt.name.match(/(\d+)/);
              if (match) {
                const duration = parseInt(match[1]);
                const mins = /hour|hr/i.test(opt.name) ? duration * 60 : duration;
                durations.push(mins);
              }
            }
          });
        }
      });
    }
    if (durations.length === 0) return null;
    const misalignedDurations = durations.filter(d => d % start_time_interval !== 0);
    if (misalignedDurations.length > 0) {
      const exampleDuration = misalignedDurations[0];
      const gapTime = start_time_interval - (exampleDuration % start_time_interval);
      const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
      const suggestedInterval = durations.reduce((acc, d) => gcd(acc, d), durations[0]);
      const validInterval = [10, 15, 30, 45, 60].find(i => i <= suggestedInterval) || 15;
      return {
        show: true,
        message: `A ${exampleDuration}-min service on a ${start_time_interval}-min schedule will leave a ${gapTime}-min gap between appointments.`,
        suggestion: `Change 'Start Time Interval' to ${validInterval} mins to minimize gaps.`
      };
    }
    return null;
  };

  const tip = getDurationMismatchTip();

  return (
    <div className="space-y-6">
      {/* Business Days & Hours */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Business Days & Hours</h3>
        <div className="space-y-3">
          {business_hours.map((hour, index) => {
            const ranges = getTimeRanges(hour);
            return (
              <div key={hour.day} className="p-3 bg-gray-50 rounded-lg space-y-2">
                {/* Day toggle + first time range */}
                <div className="flex items-center gap-4">
                  <input
                    type="checkbox"
                    checked={hour.enabled}
                    onChange={(e) => updateBusinessHour(index, 'enabled', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    data-testid={`day-${hour.day.toLowerCase()}-checkbox`}
                  />
                  <span className="w-28 text-sm font-medium text-gray-700">{hour.day}</span>
                  <Input
                    type="time"
                    value={ranges[0]?.from || '09:00'}
                    onChange={(e) => updateTimeRange(index, 0, 'from', e.target.value)}
                    disabled={!hour.enabled}
                    className="w-32"
                    data-testid={`day-${hour.day.toLowerCase()}-from`}
                  />
                  <span className="text-gray-500">to</span>
                  <Input
                    type="time"
                    value={ranges[0]?.to || '18:00'}
                    onChange={(e) => updateTimeRange(index, 0, 'to', e.target.value)}
                    disabled={!hour.enabled}
                    className="w-32"
                    data-testid={`day-${hour.day.toLowerCase()}-to`}
                  />
                </div>

                {/* Additional time ranges */}
                {ranges.length > 1 && ranges.slice(1).map((range, rIdx) => (
                  <div key={rIdx + 1} className="flex items-center gap-4 ml-[calc(1rem+7rem+1rem)]">
                    <Input
                      type="time"
                      value={range.from}
                      onChange={(e) => updateTimeRange(index, rIdx + 1, 'from', e.target.value)}
                      disabled={!hour.enabled}
                      className="w-32"
                    />
                    <span className="text-gray-500">to</span>
                    <Input
                      type="time"
                      value={range.to}
                      onChange={(e) => updateTimeRange(index, rIdx + 1, 'to', e.target.value)}
                      disabled={!hour.enabled}
                      className="w-32"
                    />
                    <button
                      type="button"
                      onClick={() => removeTimeRange(index, rIdx + 1)}
                      disabled={!hour.enabled}
                      className="p-1.5 rounded-md text-red-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}

                {/* Add Time Range button */}
                {hour.enabled && (
                  <button
                    type="button"
                    onClick={() => addTimeRange(index)}
                    data-testid={`add-range-${hour.day.toLowerCase()}`}
                    className="ml-[calc(1rem+7rem+1rem)] flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 py-1"
                  >
                    <Plus size={12} /> Add Time Range
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Scheduling Configuration — hidden for hyperlocal delivery context */}
      {!hideSchedulingSettings && (
      <div className="pt-4 border-t">
        <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
          <Info className="h-4 w-4 text-gray-400" />
          Scheduling Settings
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Start Time Interval</Label>
            <Select value={String(start_time_interval)} onValueChange={updateStartInterval}>
              <SelectTrigger data-testid="start-interval-select">
                <SelectValue placeholder="Select interval" />
              </SelectTrigger>
              <SelectContent>
                {START_INTERVAL_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={String(opt.value)}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              How frequently can a new booking start?
            </p>
          </div>

          <div className="space-y-2">
            <Label>Gap Between Bookings</Label>
            <Select value={String(buffer_between_bookings)} onValueChange={updateBufferTime}>
              <SelectTrigger data-testid="buffer-time-select">
                <SelectValue placeholder="Select gap" />
              </SelectTrigger>
              <SelectContent>
                {BUFFER_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={String(opt.value)}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">Prep/break time between sessions</p>
          </div>

          <div className="space-y-2">
            <Label>Concurrent Sessions</Label>
            <Input
              type="number"
              min="1"
              max="100"
              value={concurrent_sessions}
              onChange={(e) => updateConcurrentSessions(e.target.value)}
              className="w-full"
              data-testid="concurrent-sessions-input"
            />
            <p className="text-xs text-gray-500">
              How many sessions simultaneously? (staff/rooms)
            </p>
          </div>
        </div>

        {tip && tip.show && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
            <Lightbulb className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-amber-800">Tip</p>
              <p className="text-amber-700">{tip.message}</p>
              <p className="text-amber-600 mt-1">{tip.suggestion}</p>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default AvailabilityScheduler;
